import {
  applyStoredAmplitudeDeviceId,
  onAnalyticsReady,
} from 'uplisting-frontend/utils/analytics';
import type { IAnalytics } from 'uplisting-frontend';

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const satisfies readonly (keyof UtmParams)[];

function isAnalytics(analytics: unknown): analytics is IAnalytics {
  return !!(
    analytics &&
    typeof analytics === 'object' &&
    typeof (analytics as IAnalytics).user === 'function' &&
    typeof (analytics as IAnalytics).identify === 'function'
  );
}

const getCookie = (name: string, cookies: string) => {
  return cookies
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
};

function extractUtmParamsFromObject(
  obj: Record<string, string> = {},
): UtmParams {
  const result: UtmParams = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (UTM_PARAM_KEYS.includes(key as keyof UtmParams) && value) {
      result[key as keyof UtmParams] = value;
    }
  });

  return result;
}

function extractUtmParamsFromCookies(cookies: string): UtmParams {
  const cookie = getCookie('utmParams', cookies);
  if (!cookie) {
    return {};
  }

  try {
    const parsed = JSON.parse(window.decodeURIComponent(cookie));
    return typeof parsed === 'object' && parsed !== null
      ? extractUtmParamsFromObject(parsed)
      : {};
  } catch {
    return {};
  }
}

function extractUtmParamsFromUrl(locationSearch: string) {
  if (!locationSearch) {
    return {};
  }

  const urlParams = Object.fromEntries(
    new window.URLSearchParams(locationSearch).entries(),
  );

  return extractUtmParamsFromObject(urlParams);
}

/**
 * Tracks UTM parameters by extracting them from cookies and URL search parameters,
 * then identifies the user with these parameters using the analytics service.
 *
 * The function will extract UTM parameters from both cookies and the current URL,
 * with URL parameters taking precedence over cookie values. If no UTM parameters
 * are found or if analytics is not ready, the function will return early.
 *
 * @param analytics - The analytics instance to use for tracking. Defaults to window.analytics
 * @param locationSearch - The URL search string to extract UTM parameters from. Defaults to window.location.search
 * @param cookies - The cookie string to extract UTM parameters from. Defaults to window.document.cookie
 * @returns void
 *
 * @example
 * ```typescript
 * // Track UTM params with default values
 * trackUtmParams();
 *
 * // Track UTM params with custom analytics instance
 * trackUtmParams(customAnalytics);
 *
 * // Track UTM params with custom URL search string
 * trackUtmParams(undefined, '?utm_source=google&utm_medium=cpc');
 * ```
 */
export function trackUtmParams(
  analytics: typeof window.analytics | undefined,
  locationSearch: string,
  cookies: string,
) {
  if (!isAnalytics(analytics)) {
    return;
  }

  const params = {
    ...extractUtmParamsFromCookies(cookies),
    ...extractUtmParamsFromUrl(locationSearch),
  };

  if (Object.keys(params).length === 0) {
    return;
  }

  const user = analytics.user();
  const userId = user?.id?.();
  const args = [userId, params] as Parameters<IAnalytics['identify']>;

  analytics.identify(...args);
}

/**
 * Initializes analytics tracking for UTM parameters.
 *
 * Sets up a callback that will track UTM parameters from the current page's
 * URL search parameters and cookies once the analytics system is ready.
 */
export function initialize() {
  onAnalyticsReady(() => {
    applyStoredAmplitudeDeviceId();

    trackUtmParams(
      window.analytics,
      window.location.search,
      window.document.cookie,
    );
  });
}

export default {
  name: 'tracking',
  initialize,
};
